import { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Search, X, ArrowRight } from 'lucide-react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { pptSchedaSlug } from './PptDetailPage'

const PAGE_SIZE = 10

interface SchedaPpt {
  id: number
  area: string
  titolo: string
  descrizione: string
  indicatori: string
  azioni: string
  percentuale: string
}

interface Props {
  items: SchedaPpt[]
  areaColor?: string
}

export default function PptDialogList({ items, areaColor = '#6366f1' }: Props) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selectedItem, setSelectedItem] = useState<SchedaPpt | null>(null)

  const filtered = useMemo(() => {
    const term = search.toLowerCase()
    if (!term) return items
    return items.filter(s => {
      const text = `${s.titolo} ${s.descrizione} ${s.area} ${s.indicatori} ${s.azioni}`.toLowerCase()
      return text.includes(term)
    })
  }, [items, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  if (safePage !== page) setPage(safePage)
  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  if (items.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-3)', padding: 16, textAlign: 'center', minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Nessun obiettivo disponibile
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%', minHeight: '50vh' }}>
      {/* Search */}
      <div style={{ flexShrink: 0, position: 'relative', marginBottom: 8 }}>
        <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
        <input
          type="text"
          placeholder="Cerca obiettivo…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          style={{
            width: '100%',
            padding: '7px 8px 7px 28px',
            fontSize: 12,
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--surface-2, var(--background))',
            color: 'var(--text-1)',
            outline: 'none',
          }}
        />
      </div>

      {/* List */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'grid', gap: 2 }}>
        {paged.map(item => {
          const perc = parseFloat(item.percentuale)
          return (
            <div
              key={item.id}
              onClick={() => setSelectedItem(item)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                padding: '6px 8px',
                borderRadius: 6,
                cursor: 'pointer',
                borderLeft: `3px solid ${areaColor}`,
                background: 'var(--surface-2, transparent)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
                  {item.titolo}
                </div>
                {!isNaN(perc) && (
                  <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: areaColor }}>
                    {perc}%
                  </span>
                )}
              </div>
              {!isNaN(perc) && (
                <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{ width: `${perc}%`, height: '100%', borderRadius: 2, background: areaColor, transition: 'width 0.3s' }} />
                </div>
              )}
            </div>
          )
        })}
        {filtered.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: 16, textAlign: 'center' }}>
            Nessun obiettivo trovato
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border)',
          marginTop: 8,
        }}>
          <button type="button" disabled={safePage === 0} onClick={() => setPage(p => Math.max(0, p - 1))} style={{ background: 'none', border: 'none', cursor: safePage === 0 ? 'default' : 'pointer', color: safePage === 0 ? 'var(--text-3)' : 'var(--text-1)', padding: 4, opacity: safePage === 0 ? 0.4 : 1 }}>
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-2)' }}>{safePage + 1} / {totalPages}</span>
          <button type="button" disabled={safePage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} style={{ background: 'none', border: 'none', cursor: safePage >= totalPages - 1 ? 'default' : 'pointer', color: safePage >= totalPages - 1 ? 'var(--text-3)' : 'var(--text-1)', padding: 4, opacity: safePage >= totalPages - 1 ? 0.4 : 1 }}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Detail dialog */}
      {selectedItem && typeof document !== 'undefined' ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedItem(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 10020, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 'min(920px, 94vw)',
              maxHeight: '86vh',
              overflowY: 'auto',
              background: 'var(--tooltip-bg)',
              border: '1px solid var(--tooltip-border)',
              borderRadius: 14,
              color: 'var(--tooltip-text)',
              boxShadow: '0 14px 42px rgba(0,0,0,0.35)',
              padding: 20,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ color: 'var(--text-0)', fontSize: 14, fontWeight: 700 }}>Dettaglio obiettivo</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Link
                  to={`/poloterritoriale/${pptSchedaSlug(selectedItem.titolo)}`}
                  state={{ from: '/poloterritoriale' }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    color: 'var(--text-2)', fontSize: 12, fontWeight: 600, textDecoration: 'none',
                  }}
                >
                  Apri pagina <ArrowRight style={{ width: 14, height: 14 }} />
                </Link>
                <button
                  type="button"
                  onClick={() => setSelectedItem(null)}
                  aria-label="Chiudi"
                  style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--border-md)', background: 'var(--bg-2)', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <X size={15} />
                </button>
              </div>
            </div>
            <PptDetailContent item={selectedItem} color={areaColor} />
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

function PptDetailContent({ item, color }: { item: SchedaPpt; color: string }) {
  const perc = parseFloat(item.percentuale)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <span style={{ padding: '2px 8px', fontSize: 10, fontWeight: 600, borderRadius: 8, background: `${color}18`, color }}>
          {item.area}
        </span>
      </div>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-0)', margin: 0 }}>{item.titolo}</h2>

      {/* Progress */}
      {!isNaN(perc) && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--text-2)' }}>Stato di avanzamento</span>
            <span style={{ fontSize: 13, fontWeight: 700, color }}>{perc}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ width: `${perc}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {item.descrizione && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>Descrizione</div>
          <p style={{ fontSize: 12, color: 'var(--text-1)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>{item.descrizione}</p>
        </div>
      )}
      {item.indicatori && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>Indicatori</div>
          <p style={{ fontSize: 12, color: 'var(--text-1)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>{item.indicatori}</p>
        </div>
      )}
      {item.azioni && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4 }}>Azioni</div>
          <p style={{ fontSize: 12, color: 'var(--text-1)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>{item.azioni}</p>
        </div>
      )}
    </div>
  )
}
